import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowsClockwise, ArrowSquareOut, MagnifyingGlass, Warning, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { networkLabel } from "@/lib/networkLabels";

/**
 * Admin → Coverage Matrix
 *
 * Global publisher × network grid showing how many brands of each feed
 * partner each publisher has been given. Color intensity scales with
 * coverage % so under-covered cells pop visually. Click any pub row to
 * jump into their detail page where the per-network drill-down lives.
 */
export default function AdminCoverageMatrix() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [duplicates, setDuplicates] = useState(null);
  const [dupExpanded, setDupExpanded] = useState(false);

  const didFetch = useRef(false);

  const fetch_ = async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        API.get("/admin/coverage-matrix"),
        API.get("/admin/brands/duplicates"),
      ]);
      setMatrix(m.data);
      setDuplicates(d.data);
    } catch (e) {
      toast.error("Failed to load coverage matrix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetch_();
  }, []);

  const filteredPubs = useMemo(() => {
    if (!matrix) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return matrix.publishers;
    return matrix.publishers.filter(p =>
      (p.email || "").toLowerCase().includes(needle) ||
      (p.name || "").toLowerCase().includes(needle)
    );
  }, [matrix, q]);

  const networks = matrix?.networks || [];
  const totals = matrix?.brand_totals || {};

  // Color heat: 0 = red-50, 100 = emerald-100. Skip color when total=0.
  const cellColor = (assigned, total) => {
    if (!total) return "bg-slate-50 text-slate-400";
    const pct = (assigned / total) * 100;
    if (pct === 0) return "bg-rose-50 text-rose-700";
    if (pct < 10) return "bg-amber-50 text-amber-700";
    if (pct < 50) return "bg-yellow-50 text-yellow-800";
    return "bg-emerald-50 text-emerald-700";
  };

  return (
    <div className="space-y-6" data-testid="coverage-matrix-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Coverage Matrix</h1>
          <p className="text-sm text-slate-500 mt-1">
            How many brands each publisher has been assigned, broken out by feed partner.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={fetch_} disabled={loading} data-testid="matrix-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Cross-network duplicates banner — surfaces brand-name collisions
          between Impact / Skimlinks / mrge / Partnerize so the admin can
          catch cases like "Brooks Running synced from Impact but the pub
          is still attached to the Skimlinks row". */}
      {duplicates && (duplicates.name_collision_count > 0 || duplicates.id_collision_count > 0) && (
        <div className="border border-amber-200 bg-amber-50 rounded-md overflow-hidden" data-testid="duplicates-banner">
          <button
            type="button"
            onClick={() => setDupExpanded(v => !v)}
            className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-amber-100/60 transition-colors"
            data-testid="duplicates-toggle"
          >
            <Warning size={16} className="text-amber-700 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold text-amber-900">
                {duplicates.name_collision_count} brand name{duplicates.name_collision_count === 1 ? "" : "s"} appear in multiple feed partners
              </span>
              {duplicates.id_collision_count > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-rose-100 text-rose-700">
                  {duplicates.id_collision_count} ID collision{duplicates.id_collision_count === 1 ? "" : "s"}
                </span>
              )}
              <div className="text-xs text-amber-800 mt-0.5">
                A pub assigned via one network won't auto-pick-up the other network's row. Click to inspect.
              </div>
            </div>
            {dupExpanded ? <CaretDown size={14} className="text-amber-700" /> : <CaretRight size={14} className="text-amber-700" />}
          </button>
          {dupExpanded && (
            <div className="border-t border-amber-200 bg-white max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Feed Partners</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Active Assignments</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicates.name_collisions.map((c, idx) => (
                    <tr key={idx} className="border-b border-slate-100" data-testid={`dup-row-${idx}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{c.name}</td>
                      <td className="px-3 py-2">
                        <div className="space-y-1">
                          {c.rows.map((r, ri) => (
                            <div key={ri} className="flex items-center gap-2 text-xs">
                              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-700">
                                {networkLabel(r.network)}
                              </span>
                              <code className="text-[11px] text-slate-500 truncate max-w-[260px]">{r.campaign_id}</code>
                              {r.assigned_count > 0 && (
                                <span className="text-[10px] font-mono text-emerald-700">
                                  {r.assigned_count} pub{r.assigned_count === 1 ? "" : "s"}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">
                        {c.rows.reduce((s, r) => s + (r.assigned_count || 0), 0)}
                      </td>
                    </tr>
                  ))}
                  {duplicates.name_collisions.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-3 text-center text-slate-400 text-xs">No name collisions</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
          <MagnifyingGlass size={14} className="text-slate-400" />
          <Input
            placeholder="Filter by publisher email…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="h-7 text-sm w-72 bg-white"
            data-testid="matrix-search"
          />
          <span className="text-xs text-slate-500 ml-auto">
            {loading ? "Loading…" : `${filteredPubs.length} publishers`}
          </span>
        </div>

        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold sticky left-0 bg-slate-50 z-10">Publisher</th>
                {networks.map(n => (
                  <th key={n} className="text-center px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">
                    <div>{networkLabel(n)}</div>
                    <div className="text-[10px] font-mono font-normal text-slate-400 mt-0.5">
                      / {(totals[n] || 0).toLocaleString()}
                    </div>
                  </th>
                ))}
                <th className="text-center px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Total</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={networks.length + 3} className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filteredPubs.length === 0 && (
                <tr><td colSpan={networks.length + 3} className="px-4 py-6 text-center text-slate-400">No publishers match</td></tr>
              )}
              {!loading && filteredPubs.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`matrix-row-${p.id}`}>
                  <td className="px-4 py-2.5 sticky left-0 bg-white z-10 group-hover:bg-slate-50">
                    <div className="font-medium text-slate-900 truncate max-w-[260px]">{p.email}</div>
                    <div className="text-[11px] text-slate-400">{p.status}</div>
                  </td>
                  {networks.map(n => {
                    const assigned = p.by_network?.[n] || 0;
                    const total = totals[n] || 0;
                    return (
                      <td key={n} className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-medium ${cellColor(assigned, total)}`} data-testid={`matrix-cell-${p.id}-${n}`}>
                          {assigned}
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center font-mono text-sm font-semibold text-slate-900">
                    {p.total_assigned}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/admin/publishers/${p.id}`} className="text-[#002FA7] text-xs hover:underline inline-flex items-center gap-1">
                      Open <ArrowSquareOut size={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-3">
        <span>Cell color:</span>
        <span className="inline-block px-2 py-0.5 rounded bg-rose-50 text-rose-700">0 brands</span>
        <span className="inline-block px-2 py-0.5 rounded bg-amber-50 text-amber-700">&lt;10%</span>
        <span className="inline-block px-2 py-0.5 rounded bg-yellow-50 text-yellow-800">&lt;50%</span>
        <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">≥50%</span>
      </div>
    </div>
  );
}
